import { Component, Input } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";

import { MatDialog } from "@angular/material/dialog";

import { TranslateService } from "@ngx-translate/core";

import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { catchError, finalize, map, shareReplay } from "rxjs/operators";

import { ProductService } from "src/app/shared/services/products/products/product.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import {
  DefaultConfirmationDialogData,
  Dialog,
} from "src/app/models/dialog/dialog.model";
import { PackExtendedDTO, ProductDTO } from "src/app/models/Products/productDTO";
import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { FormUtils } from "src/app/shared/utils/formUtils";

import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";

const PACK_FORM_PARAMS = {
  productComposite: "productComposite",
  product: "product",
  quantity: "quantity",
};

@Component({
  selector: "packs-list",
  templateUrl: "./packs-list.component.html",
  styleUrls: ["./packs-list.component.scss"],
})
export class PacksListComponent {
  @Input() parentForm: FormGroup;
  @Input() set packs(packs: PackExtendedDTO[]) {
    if (!packs || !packs.length) {
      return;
    }
    this.populatePacks(packs);
  }
  @Input() currentProductCode: string; 
  @Input() duplication: boolean;

  // Product Search
  isLoadingProducts$ = new BehaviorSubject<boolean>(false);
  productList$: Observable<ProductDTO[]>;
  productList: AutocompleteSelectItem[] = [];
  searchTimer: number;

  // loaded locale
  public locale: string = localStorage.getItem("locale");

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService,
    private productService: ProductService,
    private snackbarService: SnackbarService
  ) {}

  populatePacks(packs: PackExtendedDTO[]) {
    const packsFormArray = this.getFormArray(PACK_FORM_PARAMS.productComposite);
    this.clearFormArray(packsFormArray);
    packs.forEach((pack) => {
      const packFormGroup = this.createPackFormGroup(pack);
      packsFormArray.push(packFormGroup);
    });
  }

  getFormArray(formParam: string): FormArray {
    return this.parentForm.get(formParam) as FormArray;
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
  }

  createPackFormGroup(pack?: PackExtendedDTO): FormGroup {
    const {
      productViewDTO,
      quantity,
    } = pack || {};
    return this.fb.group({
      [PACK_FORM_PARAMS.product]: [
        productViewDTO ?? null,
        [Validators.required, FormUtils.isObjectValidator],
      ],
      [PACK_FORM_PARAMS.quantity]: [
        quantity ?? null,
        [Validators.required, Validators.min(0)],
      ],
    });
  }

  addPack() {
    const packs = this.getFormArray(PACK_FORM_PARAMS.productComposite);
    const packFormGroup = this.createPackFormGroup();
    packs.push(packFormGroup);
    this.parentForm.markAsDirty();
  }

  openRemovePackDialog(index: number) {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.removePack(index);
      }
    });
  }

  removePack(index: number) {
    const packs = this.getFormArray(PACK_FORM_PARAMS.productComposite);
    packs.removeAt(index);
    if (this.packs) {
      this.packs = this.packs.filter(
        (pack, packIndex) => packIndex !== index
      );
    }
    this.parentForm.markAsDirty();
  }

  validateProductSearch() {
      this.searchProducts('');
  }

  searchProducts(event: KeyboardEvent | string, hasTimer = true): void {
    const searchTerm = typeof event === 'string' ? event : (event.target as HTMLInputElement).value;

    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => {
        this.isLoadingProducts$.next(true);
        this.productList$ = this.productService
          .getProductSearch(null, {
            filterConfig: [{ field: "code" }, { field: "description" }],
            searchTerms: [searchTerm],
          })
          .pipe(
            map(({ items }) => items.filter((product: ProductDTO) => 
              !product.isFuel && product.code !== this.currentProductCode)
            ),
            catchError((error) => {
              this.snackbarService.show({
                message: this.translate.instant("Products.loadError"),
                type: SnackbarType.WARNING,
              });
              return EMPTY;
            }),
            finalize(() => this.isLoadingProducts$.next(false)),
            shareReplay()
          );
      },
      hasTimer ? 500 : 0
    );
  }

  displayProduct(product: { code: string; description: string }): string {
    return product?.description ?? "";
  }

  openProduct(productControl: FormControl) {
    const { value: { code: productCode }} = productControl;
    if (productCode) {
      return ["/products/product", productCode];
    }
  }
}
